import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import SelectSearch from "./SelectSearch";
import ActivityLoader from "./ActivityLoader";

const TextInput = ({
  type = "text",
  label,
  labelStyle,
  wrap = false,
  style,
  leftIcon,
  leftComponent,
  loading = false,
  placeholder,
  onChange,
  onType,
  value,
  autoFocus = false,
  readonly = false,
  required = false,
  name,
  options = [],
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputType = useMemo(() => type || "text", [type]);
  const themeColor = "#4F46E5";

  // Dynamic Styles based on focus
  const wrapperStyle = {
    transition: "all 0.2s ease",
    border: `1px solid ${isFocused ? themeColor : "#E2E8F0"}`,
    borderRadius: "10px",
    overflow: "hidden",
    display: "flex",
    alignItems: "stretch",
    backgroundColor: "#FFFFFF",
    boxShadow: isFocused ? `0 0 0 3px rgba(79, 70, 229, 0.1)` : "none",
  };

  const iconBoxStyle = {
    backgroundColor: isFocused ? "#EEF2FF" : "#F8FAFC",
    borderRight: `1px solid ${isFocused ? themeColor : "#E2E8F0"}`,
    padding: "0 15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "48px",
    transition: "all 0.2s ease",
    color: isFocused ? themeColor : "#94A3B8",
  };

  const controlStyle = {
    border: "none",
    padding: "12px 16px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#1E293B",
    width: "100%",
    outline: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
  };

  return (
    <div className="form-group row" style={style}>
      {label && (
        <label
          style={{
            fontWeight: "600",
            color: "#475569",
            fontSize: "13px",
            marginBottom: "8px",
            ...labelStyle,
          }}
          className={`col-form-label ${wrap ? "col-sm-12" : "col-sm-3"}`}
        >
          {label}
        </label>
      )}

      <div className={wrap ? "col-sm-12" : "col-sm-9"}>
        <div style={wrapperStyle}>
          {leftIcon && (
            <div style={iconBoxStyle}>
              {!loading ? (
                <>
                  {!leftComponent && <i className={leftIcon} style={{ fontSize: "16px" }} />}
                  {leftComponent}
                </>
              ) : (
                <ActivityLoader inline size={16} />
              )}
            </div>
          )}

          {inputType !== "textarea" && inputType !== "select" && (
            <input
              type={inputType}
              style={controlStyle}
              placeholder={placeholder || label}
              onChange={onChange}
              value={value}
              autoFocus={autoFocus}
              readOnly={readonly}
              required={required}
              name={name}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
          )}

          {inputType === "textarea" && (
            <textarea
              style={{ ...controlStyle, minHeight: "50px" }}
              placeholder={placeholder || label}
              onChange={onChange}
              value={value}
              readOnly={readonly}
              required={required}
              name={name}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
          )}


          {inputType === "select" && (
            <div style={{ flexGrow: 1 }} onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}>
              <SelectSearch
                placeholder={placeholder || label}
                options={options}
                onChange={onChange}
                onType={onType}
                name={name}
                value={value}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ----------------------------------
 * Prop definitions
 * ---------------------------------- */
TextInput.propTypes = {
  type: PropTypes.oneOf([
    "text",
    "number",
    "email",
    "password",
    "date",
    "textarea",
    "select",
  ]),
  label: PropTypes.string,
  labelStyle: PropTypes.object,
  wrap: PropTypes.bool,
  style: PropTypes.object,

  leftIcon: PropTypes.string,
  leftComponent: PropTypes.node,
  loading: PropTypes.bool,

  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onType: PropTypes.func,

  autoFocus: PropTypes.bool,
  readonly: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string,

  // select-only
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.string,
    })
  ),
};

export default TextInput;
